import React, { useState, useEffect } from "react";
import {
  Grid, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Select, MenuItem, InputLabel, FormControl,
  useMediaQuery, Typography
} from "@mui/material";

// Error Boundary Class Component
class TransactionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.log('TransactionTab Error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Grid container spacing={2} className="max-h-[calc(100vh-210px)] overflow-y-auto">
          <Grid item xs={12} lg={12}>
            <Paper elevation={3} className="p-5 relative">
              <Typography variant="subtitle1" className="!font-semibold mb-3">
                Recent Transactions
              </Typography>
              <Paper elevation={3} className="mt-2 w-full overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Transaction ID</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={5} align="center">No data found</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    return this.props.children;
  }
}

const TransactionTabInner = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Global error handler for script errors
  useEffect(() => {
    const handleGlobalError = (event) => {
      console.log('Global error caught:', event.error);
      // Prevent the error from bubbling up
      event.preventDefault();
      return true;
    };

    const handleUnhandledRejection = (event) => {
      console.log('Unhandled promise rejection:', event.reason);
      event.preventDefault();
    };

    // Add global error listeners
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Safe search component with better error handling
  const SafeSearch = ({ value, handleInputChange }) => {
    try {
      // Check if we're in a safe environment
      if (typeof require === 'undefined') {
        throw new Error('Require not available');
      }
      
      const Search = require("../../../components/common/search/Search").default;
      return <Search value={value} handleInputChange={handleInputChange} />;
    } catch (err) {
      // Always fall back to basic input
      return (
        <input
          type="text"
          placeholder="Search transactions..."
          value={value || ""}
          onChange={handleInputChange || (() => {})}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minWidth: '200px',
            fontSize: '14px'
          }}
        />
      );
    }
  };

  // Ultra-safe data fetching
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (!isMounted) return;
        
        // Wrap fetch in additional try-catch
        const timeoutId = setTimeout(() => {
          if (isMounted) {
            setLoading(false);
          }
        }, 10000);

        const response = await fetch("https://plaid-2i1u.onrender.com/api/transactions", {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });

        clearTimeout(timeoutId);

        if (!isMounted) return;

        if (response && response.ok) {
          const data = await response.json();
          
          if (!isMounted) return;
          
          // Ultra-safe data processing
          let processedData = [];
          try {
            if (Array.isArray(data)) {
              processedData = data;
            } else if (data && typeof data === 'object') {
              if (Array.isArray(data.transactions)) {
                processedData = data.transactions;
              } else {
                // Look for any array in the response
                const possibleArrays = Object.values(data).filter(val => Array.isArray(val));
                if (possibleArrays.length > 0) {
                  processedData = possibleArrays[0];
                }
              }
            }
          } catch (parseError) {
            console.log("Data parsing error:", parseError);
          }
          
          if (isMounted) {
            setTransactions(processedData || []);
          }
        }
      } catch (error) {
        console.log("Fetch error handled:", error.message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Wrap the entire fetch call
    try {
      fetchData();
    } catch (syncError) {
      console.log("Synchronous error in useEffect:", syncError);
      if (isMounted) {
        setLoading(false);
        setTransactions([]);
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // Ultra-safe filtering
  const getFilteredTransactions = () => {
    try {
      if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
        return [];
      }

      return transactions.filter((txn) => {
        try {
          if (!txn || typeof txn !== 'object') return false;
          
          const category = String(txn.personal_finance_category?.primary || "").toLowerCase();
          const name = String(txn.name || "").toLowerCase();
          const searchTerm = String(searchQuery || "").toLowerCase();
          const filterTerm = String(filter || "").toLowerCase();
          
          const categoryMatch = !filterTerm || category.includes(filterTerm);
          const searchMatch = !searchTerm || name.includes(searchTerm);
          
          return categoryMatch && searchMatch;
        } catch (filterError) {
          return false;
        }
      });
    } catch (error) {
      return [];
    }
  };

  const filteredTransactions = getFilteredTransactions();

  const handleSearchChange = (e) => {
    try {
      setSearchQuery(e?.target?.value || "");
    } catch (error) {
      console.log("Search change error:", error);
    }
  };

  const handleFilterChange = (e) => {
    try {
      setFilter(e?.target?.value || "");
    } catch (error) {
      console.log("Filter change error:", error);
    }
  };

  return (
    <Grid container spacing={2} className="max-h-[calc(100vh-210px)] overflow-y-auto">
      <Grid item xs={12} lg={12}>
        <Paper elevation={3} className="p-5 relative">
          <div className="flex justify-between items-center mb-4">
            <SafeSearch 
              value={searchQuery} 
              handleInputChange={handleSearchChange} 
            />
            
            <FormControl variant="outlined" style={{ width: '200px' }}>
              <InputLabel style={{ lineHeight: '16px', marginTop: -4 }}>Filter By Category</InputLabel>
              <Select
                value={filter || ""}
                onChange={handleFilterChange}
                label="Filter By Category"
                style={{ height: '40px' }}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="food_and_drink">Food</MenuItem>
                <MenuItem value="entertainment">Entertainment</MenuItem>
                <MenuItem value="shopping">Shopping</MenuItem>
                <MenuItem value="transportation">Transport</MenuItem>
              </Select>
            </FormControl>
          </div>

          <Typography variant="subtitle1" className="!font-semibold mb-3">
            Recent Transactions
          </Typography>

          <Paper elevation={3} className="mt-2 w-full overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Transaction ID</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">Loading transactions...</TableCell>
                  </TableRow>
                ) : filteredTransactions.length > 0 ? (
                  filteredTransactions.map((txn, idx) => {
                    try {
                      const safeAmount = Number(txn?.amount || 0);
                      return (
                        <TableRow key={String(txn?.transaction_id || `txn-${idx}`)}>
                          <TableCell>{String(txn?.transaction_id || "-")}</TableCell>
                          <TableCell>{String(txn?.date || "-")}</TableCell>
                          <TableCell>${isNaN(safeAmount) ? "0.00" : safeAmount.toFixed(2)}</TableCell>
                          <TableCell>{String(txn?.personal_finance_category?.primary || "-")}</TableCell>
                          <TableCell>{String(txn?.name || "-")}</TableCell>
                        </TableRow>
                      );
                    } catch (rowError) {
                      return (
                        <TableRow key={`error-row-${idx}`}>
                          <TableCell colSpan={5} align="center">-</TableCell>
                        </TableRow>
                      );
                    }
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No data found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Paper>
      </Grid>
    </Grid>
  );
};

// Main component wrapped with error boundary
const TransactionTab = () => {
  return (
    <TransactionErrorBoundary>
      <TransactionTabInner />
    </TransactionErrorBoundary>
  );
};

export default TransactionTab;